// Auth Store - User authentication state management
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '../api/types';
import authApi from '../api/auth';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.is_admin ?? false);
  const displayName = computed(() => user.value?.display_name ?? 'Guest');

  // Actions
  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await authApi.login({ email, password });
      user.value = response.user;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function loginWithGoogle(idToken: string): Promise<void> {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await authApi.loginWithGoogle(idToken);
      user.value = response.user;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Google login failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function register(email: string, password: string, displayName: string): Promise<void> {
    loading.value = true;
    error.value = null;
    
    try {
      await authApi.register({ email, password, display_name: displayName });
      // Auto-login after registration
      await login(email, password);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Registration failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    loading.value = true;
    
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      user.value = null;
      loading.value = false;
    }
  }

  async function fetchCurrentUser(): Promise<void> {
    loading.value = true;
    
    try {
      user.value = await authApi.getCurrentUser();
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  function clearError(): void {
    error.value = null;
  }

  return {
    // State
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    displayName,
    // Actions
    login,
    loginWithGoogle,
    register,
    logout,
    fetchCurrentUser,
    clearError,
  };
}, {
  persist: {
    key: 'tamil-soundboard-auth',
    paths: ['user'],
  },
});

export default useAuthStore;
